/**
 * MaestroTiposRecurso
 * Página para gestionar el maestro de Tipos de Recurso
 * CRUD completo para crear, editar, listar y eliminar tipos de recurso
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiPlus, FiEdit2, FiTrash2, FiX } from 'react-icons/fi';
import { toast } from 'react-toastify';
import { tipoRecursoService } from '../../services/catalogoService';
import Loading from '../../components/Loading';
import '../../styles/maestros.css';

const MaestroTiposRecurso = () => {
  const navigate = useNavigate();
  const [tiposRecurso, setTiposRecurso] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [modoEdicion, setModoEdicion] = useState(false);
  const [tipoSeleccionado, setTipoSeleccionado] = useState(null);
  const [formData, setFormData] = useState({ nombre: '' });

  useEffect(() => {
    cargarTiposRecurso();
  }, []);

  const cargarTiposRecurso = async () => {
    setLoading(true);
    try {
      const response = await tipoRecursoService.getAll();
      const data = Array.isArray(response.data) ? response.data : (response.data.results || []);
      setTiposRecurso(data);
    } catch (error) {
      console.error('Error al cargar tipos de recurso:', error);
      toast.error('Error al cargar los tipos de recurso');
    } finally {
      setLoading(false);
    }
  };

  const abrirModalCrear = () => {
    setFormData({ nombre: '' });
    setModoEdicion(false);
    setTipoSeleccionado(null);
    setModalOpen(true);
  };

  const abrirModalEditar = (tipo) => {
    setFormData({ nombre: tipo.nombre });
    setModoEdicion(true);
    setTipoSeleccionado(tipo);
    setModalOpen(true);
  };

  const cerrarModal = () => {
    setModalOpen(false);
    setFormData({ nombre: '' });
    setTipoSeleccionado(null);
    setModoEdicion(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.nombre.trim()) {
      toast.error('El nombre es requerido');
      return;
    }

    try {
      if (modoEdicion) {
        await tipoRecursoService.update(tipoSeleccionado.id, formData);
        toast.success('Tipo de recurso actualizado exitosamente');
      } else {
        await tipoRecursoService.create(formData);
        toast.success('Tipo de recurso creado exitosamente');
      }
      cerrarModal();
      cargarTiposRecurso();
    } catch (error) {
      console.error('Error al guardar tipo de recurso:', error);
      toast.error(error.response?.data?.detail || 'Error al guardar el tipo de recurso');
    }
  };

  const handleEliminar = async (tipoId) => {
    if (!window.confirm('¿Estás seguro de que deseas eliminar este tipo de recurso?')) {
      return;
    }

    try {
      await tipoRecursoService.delete(tipoId);
      toast.success('Tipo de recurso eliminado exitosamente');
      cargarTiposRecurso();
    } catch (error) {
      console.error('Error al eliminar tipo de recurso:', error);
      toast.error('Error al eliminar el tipo de recurso');
    }
  };

  if (loading) {
    return <Loading />;
  }

  return (
    <div className="maestro-areas">
      <div className="header">
        <div>
          <h1>Tipos de Recurso</h1>
          <p style={{ margin: '0.5rem 0 0 0', color: '#666', fontSize: '0.95rem' }}>
            Total de tipos: <strong>{tiposRecurso.length}</strong>
          </p>
        </div>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <button
            className="btn btn-secondary"
            onClick={() => navigate('/onebase/maestros/recursos')}
            title="Gestionar recursos"
          >
            Gestionar Recursos
          </button>
          <button
            className="btn btn-primary"
            onClick={abrirModalCrear}
          >
            <FiPlus /> Nuevo Tipo
          </button>
        </div>
      </div>

      {tiposRecurso.length === 0 ? (
        <div className="empty-state">
          <p>No hay tipos de recurso creados aún</p>
          <button
            className="btn btn-primary"
            onClick={abrirModalCrear}
          >
            Crear Primer Tipo
          </button>
        </div>
      ) : (
        <div className="areas-list">
          {tiposRecurso.map(tipo => (
            <div key={tipo.id} className="area-item">
              <div className="area-header">
                <div className="area-info">
                  <h3>{tipo.nombre}</h3>
                  {tipo.created_by_nombre && (
                    <p className="description" style={{ fontSize: '0.85rem', color: '#999' }}>
                      Creado por: {tipo.created_by_nombre}
                    </p>
                  )}
                </div>

                <div className="actions">
                  <button
                    className="btn btn-sm btn-secondary"
                    onClick={() => abrirModalEditar(tipo)}
                    title="Editar tipo"
                  >
                    <FiEdit2 />
                  </button>
                  <button
                    className="btn btn-sm btn-danger"
                    onClick={() => handleEliminar(tipo.id)}
                    title="Eliminar tipo"
                  >
                    <FiTrash2 />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal Simple */}
      {modalOpen && (
        <div className="recurso-modal-overlay" onClick={cerrarModal}>
          <div className="recurso-modal" style={{ maxWidth: '500px' }} onClick={(e) => e.stopPropagation()}>
            <div className="recurso-modal-header">
              <h3>{modoEdicion ? 'Editar Tipo de Recurso' : 'Nuevo Tipo de Recurso'}</h3>
              <button onClick={cerrarModal} className="modal-close-btn">
                <FiX />
              </button>
            </div>

            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label htmlFor="nombre">Nombre *</label>
                <input
                  id="nombre"
                  type="text"
                  name="nombre"
                  value={formData.nombre}
                  onChange={(e) => setFormData({ nombre: e.target.value })}
                  placeholder="ej: Mano de obra, Materiales, Equipos"
                  autoFocus
                />
              </div>

              <div className="modal-footer">
                <button type="button" onClick={cerrarModal} className="btn btn-secondary">
                  Cancelar
                </button>
                <button type="submit" className="btn btn-primary">
                  {modoEdicion ? 'Actualizar' : 'Crear'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default MaestroTiposRecurso;
